import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import type { FolderTreeNode } from "@bi-studio/shared/lib";
import { Tree, Group, Text, ThemeIcon, useTree, type TreeNodeData, useMantineColorScheme } from "@mantine/core";
import { ReportIcon, BarChartIcon, LineChartIcon, BarLineChartIcon, RadarChartIcon, StackedLineChartIcon, FormIcon, PieChartIcon, DashboardIcon, GaugeChartIcon, TabGroupIcon, FolderIcon, ScoreCardIcon, AreaChartIcon, CalendarChartIcon, MapChartIcon } from "@bi-studio/shared/components";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchRootContent, selectAllFolders, selectRootLoading, selectRootLoaded, selectFolderError } from "@/store/slices/folderSlice";

// Transform FolderTreeNode to Mantine Tree format
const transformFolderData = (folders: FolderTreeNode[]): TreeNodeData[] => {
   return folders.map((folder) => {
      const children: TreeNodeData[] = [
         ...(folder.children && folder.children.length > 0
            ? transformFolderData(folder.children)
            : []),
         ...(folder.dashboards?.map(d => ({
            label: d.name,
            value: `dashboard:${d.id}`,
            nodeProps: { isFile: true, type: 'dashboard', id: d.id }
         })) || []),
         ...(folder.reports?.map(r => ({
            label: r.name,
            value: `report:${r.id}`,
            nodeProps: { isFile: true, type: 'report', id: r.id }
         })) || []),
         // Note: charts are usually under reports, but can be folder items
         ...((folder as any).charts?.map((c: any) => ({
            label: c.config?.title || `Chart ${c.id}`,
            value: `chart:${c.id}`,
            nodeProps: { isFile: true, type: 'chart', chartType: c.type, id: c.id }
         })) || []),
         ...(folder.forms?.map(f => ({
            label: f.name,
            value: `form:${f.id}`,
            nodeProps: { isFile: true, type: 'form', id: f.id }
         })) || [])
      ];

      return {
         label: folder.name,
         value: folder.id,
         children: children.length > 0 ? children : undefined,
      };
   });
};

export function FolderTree() {
   const { token } = useAuth();
   const navigate = useNavigate();
   const location = useLocation();
   const pathname = location.pathname;
   const dispatch = useAppDispatch();
   const { colorScheme } = useMantineColorScheme();
   const folders = useAppSelector(selectAllFolders);
   const isLoading = useAppSelector(selectRootLoading);
   const isLoaded = useAppSelector(selectRootLoaded);
   const error = useAppSelector(selectFolderError);

   const treeData = React.useMemo(() => transformFolderData(folders), [folders]);
   const tree = useTree();

   // Handle folder IDs from pathname
   React.useEffect(() => {
      if (pathname.startsWith("/folders/")) {
         const folderId = pathname.replace("/folders/", "");
         tree.select(folderId);

         const expandParents = (data: TreeNodeData[], targetId: string): boolean => {
            for (const node of data) {
               if (node.value === targetId) return true;
               if (node.children) {
                  const isChildSelected = expandParents(node.children, targetId);
                  if (isChildSelected) {
                     tree.expand(node.value);
                     return true;
                  }
               }
            }
            return false;
         };

         expandParents(treeData, folderId);
      } else if (pathname === "/folders") {
         tree.setSelectedState([]);
      }
   }, [pathname, treeData]);

   React.useEffect(() => {
      if (!token) return;

      const loadFolders = () => {
         dispatch(fetchRootContent(token));
      };

      // Only load if not already loaded
      if (!isLoaded && !isLoading && !error) {
         loadFolders();
      }

      const handleRefresh = () => {
         loadFolders();
      };
      window.addEventListener("refresh-folders", handleRefresh);
      return () => window.removeEventListener("refresh-folders", handleRefresh);
   }, [token, dispatch, folders.length, isLoading, error]);

   const content = React.useMemo(() => {
      if (isLoading) {
         return (
            <Text size="sm" c="dimmed" ta="center" py="xl">
               Loading folders...
            </Text>
         );
      }

      if (error) {
         return (
            <Text size="sm" c="red" ta="center" py="xl">
               {error}
            </Text>
         );
      }

      if (folders.length === 0) {
         return (
            <Text size="sm" c="dimmed" ta="center" py="xl">
               No folders available
            </Text>
         );
      }

      return (
         <Tree
            tree={tree}
            data={treeData}
            levelOffset={20}
            renderNode={({ node, elementProps, level }) => {
               const isFile = (node.nodeProps as any)?.isFile;
               const nodeType = (node.nodeProps as any)?.type;
               const nodeId = (node.nodeProps as any)?.id;
               const isSelected = tree.selectedState.includes(node.value);

               return (
                  <Group
                     gap={8}
                     {...elementProps}
                     onClick={(e) => {
                        elementProps.onClick?.(e);
                        if (isFile && nodeType && nodeId) {
                           if (nodeType === 'report') navigate(`/reports/${nodeId}`);
                           else if (nodeType === 'dashboard') navigate(`/dashboards/${nodeId}`);
                           else if (nodeType === 'chart') navigate(`/charts/${nodeId}`);
                           else if (nodeType === 'form') navigate(`/forms/${nodeId}`);
                        } else {
                           navigate(`/folders/${node.value}`);
                        }
                     }}
                     style={{
                        ...elementProps.style,
                        cursor: 'pointer',
                        padding: '4px 8px',
                        paddingLeft: `calc(${level} * 20px + 8px)`,
                        borderRadius: '4px',
                        backgroundColor: isSelected
                           ? (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'var(--mantine-color-brand-light)')
                           : 'transparent',
                        marginRight: '8px',
                        color: isFile ? 'var(--mantine-color-dimmed)' : 'var(--mantine-color-text)'
                     }}
                  >
                     <ThemeIcon
                        variant="subtle"
                        color={isSelected ? 'brand' : 'gray'}
                        size="sm"
                     >
                        {isFile ? (
                           <>
                              {nodeType === 'report' && <ReportIcon size={14} />}
                              {nodeType === 'dashboard' && <DashboardIcon size={14} />}
                              {nodeType === 'chart' && (
                                 (() => {
                                    const chartType = (node.nodeProps as any)?.chartType;
                                    if (chartType === 'line') return <LineChartIcon size={14} />;
                                    if (chartType === 'gauge') return <GaugeChartIcon size={14} />;
                                    if (chartType === 'pie') return <PieChartIcon size={14} />;
                                    if (chartType === 'stacked-line') return <StackedLineChartIcon size={14} />;
                                    if (chartType === 'stacked-bar-line') return <BarLineChartIcon size={14} />;
                                    if (chartType === 'radar') return <RadarChartIcon size={14} />;
                                    if (chartType === 'scorecard') return <ScoreCardIcon size={14} />;
                                    if (chartType === 'area') return <AreaChartIcon size={14} />;
                                    if (chartType === 'calendar') return <CalendarChartIcon size={14} />;
                                    if (chartType === 'map') return <MapChartIcon size={14} />;
                                    return <BarChartIcon size={14} />;
                                 })()
                              )}
                              {nodeType === 'form' && <FormIcon size={14} />}
                              {nodeType === 'tab-group' && <TabGroupIcon size={14} />}
                           </>
                        ) : (
                           <FolderIcon size={14} />
                        )}
                     </ThemeIcon>
                     <Text size="sm" fw={isSelected ? 500 : 400}>
                        {node.label}
                     </Text>
                  </Group>
               );
            }}
         />
      );
   }, [isLoading, error, folders.length, tree, treeData, navigate]);

   return (
      <>
         {content}
      </>
   );
}

export function useFolderRefresh() {
   return React.useCallback(() => {
      window.dispatchEvent(new CustomEvent("refresh-folders"));
   }, []);
}

